import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useCallback, useEffect, useState } from "react";
import { CircularProgress, Stack, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { useNavigate } from "react-router-dom";
import {
  campaignsApi,
  useGetCampaignDetailsQuery,
  useStartAccountPlanGenerationMutation,
  useUpdateAccountPlanGenerationMutation,
} from "../../../api";
import { CampaignDetailsResponse } from "../../../types/apiTypes";
import {
  AccountPlanStatus,
  AppRoutes,
  ButtonsColorType,
  ButtonsType,
  PlaybookPlanNames,
} from "../../../types/enums";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import {
  setIsFetchTableMembers,
  setIsNotificationsOpen,
} from "../../../features/companySlice";
import { selectPermissions } from "../../../features/permissionsSlice";
import { ReuseButton } from "../ReuseButton";

export const StartGenerateAccountPlanBtn = ({
  account_id,
  account,
  fullWidth,
  campaign_id,
}: {
  account_id: string;
  campaign_id: string;
  account?: CampaignDetailsResponse;
  fullWidth?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isGeneratingReport = !!account?.is_generating_account_plan;
  const hasReport = !!account?.has_account_plan;
  const [isGenerating, setIsGenerating] = useState<boolean>(isGeneratingReport);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(hasReport);

  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const { isAllowedEditGenerateReports, isAllowedViewAccountPlans } =
    useAppSelector(selectPermissions);

  const accountPlanStatus = useAppSelector(
    (state) => state.company.status.account_plan_status,
  );

  const { data: campaignDetails, isLoading: isCampaignDetailsLoading } =
    useGetCampaignDetailsQuery({ account_id: account_id! });

  useEffect(() => {
    setIsUpdating(
      campaignDetails?.account_plan_status === AccountPlanStatus.updating,
    );
  }, [campaignDetails?.account_plan_status]);

  const [startGeneration, { isLoading }] =
    useStartAccountPlanGenerationMutation();

  const [updateAccountPlan] = useUpdateAccountPlanGenerationMutation();

  useEffect(() => {
    setIsCompleted(hasReport);
    setIsGenerating(isGeneratingReport);
  }, [hasReport, isGeneratingReport]);

  useEffect(() => {
    if (accountPlanStatus?.account_id === account_id) {
      switch (accountPlanStatus.status) {
        case "in_progress":
          setIsGenerating(true);
          setIsCompleted(false);
          break;

        case "completed":
          setIsGenerating(false);
          setIsCompleted(true);
          break;

        case "failed":
          setIsGenerating(false);
          setIsCompleted(false);
          break;
      }
    }
  }, [accountPlanStatus, account_id]);

  const handleStartGeneration = useCallback(async () => {
    await startGeneration({
      account_id,
      campaign_id,
    })
      .unwrap()
      .then(() => {
        setIsGenerating(true);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to Start"
            text={
              getErrorMessage(err).globalError || "Failed to start generation"
            }
          />,
        );
      });

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
    dispatch(setIsNotificationsOpen(true));
  }, [account_id, startGeneration, dispatch]);

  const handleViewPlan = useCallback(() => {
    navigate(`/${campaign_id}${AppRoutes.ACCOUNT_PLAN}/${account_id}`);
  }, [account_id, campaign_id, navigate]);

  const updateAccountPlanHandler = () => {
    setIsUpdating(true);

    updateAccountPlan({
      campaign_id,
      account_ids: [account_id],
    })
      .unwrap()
      .then(() => {
        dispatch(campaignsApi.util.invalidateTags(["activities", "company"]));
        dispatch(setIsFetchTableMembers(true));
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to Update"
            text={
              getErrorMessage(err).globalError || "Failed to update generation"
            }
          />,
        );
        setIsUpdating(false);
      });
  };

  if (isCompleted) {
    return (
      <Stack gap="12px">
        <ReuseButton
          onClick={handleViewPlan}
          buttonColor={ButtonsColorType.dark_orange}
          disabled={!isAllowedViewAccountPlans}
        >
          Check Current {PlaybookPlanNames.account_plan}
        </ReuseButton>

        {campaignDetails?.has_committee_changes && isEditor && (
          <ReuseButton
            onClick={updateAccountPlanHandler}
            buttonColor={ButtonsColorType.gradient}
            buttonType={ButtonsType.contained}
            disabled={
              isUpdating ||
              isCampaignDetailsLoading ||
              !isAllowedEditGenerateReports
            }
            startIcon={
              (isUpdating || isCampaignDetailsLoading) && (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              )
            }
          >
            Update {PlaybookPlanNames.account_plan}
          </ReuseButton>
        )}
      </Stack>
    );
  }

  const button = (
    <ReuseButton
      onClick={handleStartGeneration}
      buttonColor={ButtonsColorType.gradient}
      buttonType={ButtonsType.contained}
      disabled={isLoading || isGenerating || !isAllowedEditGenerateReports}
      startIcon={
        (isLoading || isGenerating) && (
          <CircularProgress size={24} sx={{ color: "inherit" }} />
        )
      }
    >
      {isLoading || isGenerating
        ? `Preparing ${PlaybookPlanNames.account_plan}`
        : `Get Target ${PlaybookPlanNames.account_plan}`}
    </ReuseButton>
  );

  if (isLoading || isGenerating) {
    return (
      <Tooltip
        placement="top"
        sx={{
          "& .MuiTooltip-tooltip": {
            background: "#596177E5",
            fontSize: "12px",
            lineHeight: "16px",
          },
        }}
        arrow
        title={`Your ${PlaybookPlanNames.account_plan} is in progress. We'll notify you by email when it's done.`}
      >
        <span
          style={{
            width: fullWidth ? "100%" : "auto",
            display: "inline-block",
          }}
        >
          {button}
        </span>
      </Tooltip>
    );
  }

  return button;
};
